"use strict";
import { jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useRef, useState } from "react";
import { useEffect } from "react";
import { Route, Routes, useLocation } from "react-router";
import { Button, Buttons, Link, Tooltip } from "capitalrx-components-2.0/lib/general";
import * as Color from "capitalrx-components-2.0/lib/general/Color";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Dashboard } from "capitalrx-types";
import classNames from "classnames";
import { useLayout } from "../../ContentLayout";
import { useContentChannel } from "../../components/ContentChannel";
import { useShellModulePathResolver } from "../../hooks";
import { useAuthToken } from "../../hooks";
import { Actions } from "./Actions";
import { Logo } from "./Logo";
import { Menu } from "./Menu";
import { Notifications } from "./Notifications";
import { getNavigationGroupClassName } from "./getNavigationGroupClassName";
import { useMenuGroups, useMenuItems } from "./hooks";
import styles from "./MenuBar.module.css";
const MenuButtonAppearance = {
  ...Buttons.Functional.Tertiary.Large,
  normal: {
    ...Buttons.Functional.Tertiary.Large.normal,
    foregroundColor: Color.White
  },
  hover: {
    ...Buttons.Functional.Tertiary.Large.hover,
    foregroundColor: Color.Turqoise["100"]
  },
  pressed: {
    ...Buttons.Functional.Tertiary.Large.pressed,
    foregroundColor: Color.White
  }
};
const ExpandedMenuButtonAppearance = {
  ...MenuButtonAppearance,
  normal: {
    ...Buttons.Functional.Tertiary.Large.normal,
    foregroundColor: Color.Turqoise["100"]
  }
};
export function getMenuTitleRoutePattern(module, data, resolvePath) {
  var _a;
  const { path, absolutePath = false } = data;
  if (!absolutePath) {
    const moduleRoot = (_a = resolvePath(module, "/")) != null ? _a : "/";
    return moduleRoot.endsWith("/") ? `${moduleRoot}*` : `${moduleRoot}/*`;
  }
  let resolvedPath = path;
  if (resolvedPath && resolvedPath.startsWith("~")) {
    resolvedPath = resolvedPath.substring(1);
  }
  resolvedPath = resolvedPath != null ? resolvedPath : "/";
  return resolvedPath.endsWith("/") ? `${resolvedPath}*` : `${resolvedPath}/*`;
}
export const MenuBar = () => {
  const [expanded, setExpanded] = useState(false);
  const buttonContainerRef = useRef(null);
  const menuRef = useRef(null);
  const location = useLocation();
  const resolvePath = useShellModulePathResolver();
  const actionBars = useContentChannel(null, "app-action-bars");
  const layoutType = useLayout();
  const { menuColumnSpan, nameVisible } = useResponsive({
    menuColumnSpan: { desktop: 3, tablet: 6, mobile: 12 },
    nameVisible: { desktop: true, tablet: true, mobile: false }
  });
  const [authToken, _] = useAuthToken();
  let identity = "";
  if (authToken) {
    const parts = authToken.split(".");
    const payload = JSON.parse(atob(parts[1]));
    identity = payload.username;
  }
  const items = useMenuItems();
  const columnGroups = useMenuGroups(items, 12 / menuColumnSpan);
  const clickHandler = useCallback(() => {
    setExpanded((previousExpanded) => !previousExpanded);
    if (buttonContainerRef.current) {
      buttonContainerRef.current.focus();
    }
  }, []);
  const mouseDownHandler = useCallback((ev) => {
    ev.preventDefault();
  }, []);
  const blurHandler = useCallback((ev) => {
    const { relatedTarget } = ev;
    if (relatedTarget) {
      if (relatedTarget === (buttonContainerRef == null ? void 0 : buttonContainerRef.current)) return;
      if (relatedTarget === (menuRef == null ? void 0 : menuRef.current) || menuRef.current && menuRef.current.contains(relatedTarget)) return;
    }
    setExpanded(false);
  }, []);
  useEffect(() => {
    function listener() {
      setExpanded(false);
    }
    if (expanded) {
      document.addEventListener("scroll", listener);
      globalThis.addEventListener("resize", listener);
    }
    return () => {
      document.removeEventListener("scroll", listener);
      globalThis.removeEventListener("resize", listener);
    };
  }, [expanded]);
  useEffect(() => {
    setExpanded(false);
  }, [location]);
  if (layoutType.startsWith("fullscreen")) {
    return null;
  }
  const columnElements = columnGroups.map((groups, columnIndex) => {
    return /* @__PURE__ */ jsx(Grid.Span, { columns: menuColumnSpan, children: /* @__PURE__ */ jsx(Stack, { className: "column", spacing: "large", children: groups.map((group) => {
      var _a;
      const groupData = (_a = Dashboard.NavigationGroup[group.id]) != null ? _a : Dashboard.NavigationGroup.Unknown;
      return /* @__PURE__ */ jsxs(Stack, { className: classNames("group", getNavigationGroupClassName(groupData)), spacing: "mini", children: [
        /* @__PURE__ */ jsx("h3", { className: "header", style: { color: groupData.color }, children: groupData.name }),
        /* @__PURE__ */ jsx(Stack, { spacing: "mini", children: group.items.map((item, index) => {
          var _a2;
          const { module, data } = item;
          const { path, testId, title, beta = false, absolutePath = false, internalUseOnly = false } = data;
          const resolvedPath = absolutePath ? path != null ? path : "/" : (_a2 = resolvePath(module, path)) != null ? _a2 : "/";
          let canViewItem = true;
          if (internalUseOnly && !identity.endsWith("@cap-rx.com")) {
            canViewItem = false;
          }
          if (canViewItem) {
            return /* @__PURE__ */ jsxs(Shelf, { spacing: "mini", children: [
              /* @__PURE__ */ jsx(Link, { to: resolvedPath, testId, tabIndex: 2, children: title }),
              beta && /* @__PURE__ */ jsx("span", { className: "beta", children: "BETA" }),
              /* @__PURE__ */ jsx(Shelf.Spacer, {})
            ] }, index);
          }
        }) })
      ] }, groupData.id);
    }) }, columnIndex) }, columnIndex);
  });
  return /* @__PURE__ */ jsx("nav", { "data-testid": "menubar", className: classNames("menu-bar", styles.menuBarRoot, { [styles.hasActionBars]: actionBars.length > 0 }), children: /* @__PURE__ */ jsxs("div", { className: "bar-container", tabIndex: -1, onBlur: blurHandler, children: [
    /* @__PURE__ */ jsxs(Shelf, { className: "bar", alignment: "center", children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          className: "menu-button",
          ref: buttonContainerRef,
          onClickCapture: clickHandler,
          onBlurCapture: blurHandler,
          onMouseDown: mouseDownHandler,
          tabIndex: 1,
          children: /* @__PURE__ */ jsx(Button, { icon: "menu-grid", appearance: expanded ? ExpandedMenuButtonAppearance : MenuButtonAppearance, tabIndex: -1 })
        }
      ),
      /* @__PURE__ */ jsx(Logo, {}),
      nameVisible && /* @__PURE__ */ jsx(Shelf, { alignment: "span", children: /* @__PURE__ */ jsxs(Routes, { children: [
        items.map((item, index) => {
          const { data, module } = item;
          const { title, description, path, absolutePath = false } = data;
          const resolvedPath = getMenuTitleRoutePattern(module, { path, absolutePath }, resolvePath);
          return /* @__PURE__ */ jsx(
            Route,
            {
              path: resolvedPath,
              element: /* @__PURE__ */ jsx(Tooltip, { content: description, alignment: "bottom", children: (ref) => /* @__PURE__ */ jsx("div", { className: "module-name", ref, children: title }) })
            },
            index
          );
        }),
        /* @__PURE__ */ jsx(Route, { path: "*", element: null })
      ] }) }),
      /* @__PURE__ */ jsx(Shelf.Spacer, {}),
      /* @__PURE__ */ jsxs(Shelf, { spacing: "none", children: [
        /* @__PURE__ */ jsx(Notifications, {}),
        /* @__PURE__ */ jsx(Actions, {})
      ] })
    ] }),
    /* @__PURE__ */ jsx(Menu, { ref: menuRef, expanded, children: /* @__PURE__ */ jsx(Grid, { className: styles.menuGridRoot, spacing: "none", children: columnElements }) })
  ] }) });
};
